import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ExternalLink, Loader2, Search } from 'lucide-react';
import { Modal, Button, Input, Label, Switch } from '@/components/ui';
import { useInstallPlugin, useExplorePlugin } from '../../hooks/usePlugins';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../services/api';
import type {
  ExploreCandidate,
  ExploreResponse,
  PluginInstallAck,
  PluginInstallReport,
  PluginMode,
} from '../../types';
import ImportReportPanel from './ImportReportPanel';
import ExploreResultPanel from './ExploreResultPanel';

interface InstallPluginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ErrorState {
  code?: string;
  message: string;
}

type Phase = 'idle' | 'exploring' | 'ready' | 'installing';

const REEXPLORE_DEBOUNCE_MS = 300;

function isLikelyRepoUrl(url: string): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith('git@')) return /:.+\/.+/.test(trimmed);
  try {
    const parsed = new URL(trimmed);
    return ['http:', 'https:', 'ssh:', 'git:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function pickInitialCandidate(
  candidates: ExploreCandidate[],
  preferredSubdir: string | undefined,
): ExploreCandidate | undefined {
  if (candidates.length === 0) return undefined;
  if (preferredSubdir !== undefined) {
    const match = candidates.find((c) => c.subdir === preferredSubdir);
    if (match) return match;
  }
  const fromMarketplace = candidates.find((c) => c.source === 'marketplace');
  return fromMarketplace ?? candidates[0];
}

export default function InstallPluginModal({ isOpen, onClose }: InstallPluginModalProps) {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success } = useToast();
  const explore = useExplorePlugin();
  const install = useInstallPlugin();

  const [phase, setPhase] = useState<Phase>('idle');
  const [mode, setMode] = useState<PluginMode>('plugin');
  // Default ON — matches the spec ("auto-update should be the default"). Hidden
  // when the explored ref is a SHA, which the backend always treats as pinned.
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [repoUrl, setRepoUrl] = useState('');
  const [exploreResult, setExploreResult] = useState<ExploreResponse | null>(null);
  const [selectedRef, setSelectedRef] = useState<string>('');
  const [selectedSubdir, setSelectedSubdir] = useState<string | undefined>(undefined);
  const [manualSubdir, setManualSubdir] = useState('');
  const [exploreError, setExploreError] = useState<ErrorState | null>(null);
  const [installError, setInstallError] = useState<ErrorState | null>(null);
  const [submittedUrl, setSubmittedUrl] = useState(false);
  const [report, setReport] = useState<PluginInstallReport | null>(null);

  // Track which ref last triggered a successful explore so the debounce
  // effect skips re-firing for the value we just rendered.
  const exploredRefRef = useRef<string>('');

  function reset() {
    setPhase('idle');
    setMode('plugin');
    setRepoUrl('');
    setExploreResult(null);
    setSelectedRef('');
    setSelectedSubdir(undefined);
    setManualSubdir('');
    setExploreError(null);
    setInstallError(null);
    setSubmittedUrl(false);
    setReport(null);
    exploredRefRef.current = '';
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function runExplore(
    url: string,
    ref: string | undefined,
    preferredSubdir: string | undefined,
  ) {
    setPhase('exploring');
    setExploreError(null);
    try {
      const result = await explore.mutateAsync({
        repoUrl: url,
        ref: ref || undefined,
      });
      const candidate = pickInitialCandidate(result.candidates, preferredSubdir);
      setExploreResult(result);
      setSelectedRef(result.ref);
      setSelectedSubdir(candidate?.subdir);
      exploredRefRef.current = result.ref;
      setPhase('ready');
    } catch (err) {
      if (err instanceof ApiError) {
        setExploreError({ code: err.code, message: err.message });
      } else {
        setExploreError({
          message: err instanceof Error ? err.message : 'Explore failed',
        });
      }
      // Leave whatever was already shown in place so the user can fix the
      // ref and try again; don't reset to idle.
      setPhase(exploreResult ? 'ready' : 'idle');
    }
  }

  async function handleExplore() {
    setSubmittedUrl(true);
    if (!isLikelyRepoUrl(repoUrl)) {
      setExploreError({
        code: 'VALIDATION_ERROR',
        message: 'Enter a valid git repository URL.',
      });
      return;
    }
    await runExplore(repoUrl.trim(), undefined, undefined);
  }

  // Auto re-explore when the user picks a different ref. Debounced so rapid
  // dropdown changes only fire once.
  useEffect(() => {
    if (phase === 'idle' || phase === 'installing') return;
    if (!exploreResult) return;
    if (selectedRef === exploredRefRef.current) return;

    const timer = setTimeout(() => {
      runExplore(repoUrl.trim(), selectedRef, selectedSubdir);
    }, REEXPLORE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRef]);

  function handleRepoUrlChange(value: string) {
    setRepoUrl(value);
    // Any URL edit invalidates the prior explore.
    if (exploreResult) {
      setExploreResult(null);
      setSelectedRef('');
      setSelectedSubdir(undefined);
      exploredRefRef.current = '';
      setPhase('idle');
    }
    if (exploreError) setExploreError(null);
  }

  async function handleInstall() {
    setInstallError(null);
    const subdir =
      exploreResult && exploreResult.candidates.length > 0
        ? selectedSubdir ?? ''
        : manualSubdir;
    // Capture the submit mode — the discriminator. The backend's plugin-mode
    // 202 response is a bare `{ pluginId, status }` ack without echoing
    // `mode`, so we can't reliably narrow off the response itself.
    const submitMode = mode;
    // Seed the row from the explore candidate so a failed install still
    // shows a real name/description rather than just the pluginId. Server
    // overwrites these with the authoritative manifest values on success.
    const selectedCandidate = exploreResult?.candidates.find(
      (c) => c.subdir === selectedSubdir,
    );
    const metadata = selectedCandidate
      ? {
          name: selectedCandidate.name,
          displayName: selectedCandidate.displayName,
          version: selectedCandidate.version,
          description: selectedCandidate.description,
          author: selectedCandidate.author,
        }
      : undefined;
    setPhase('installing');
    try {
      const result = await install.mutateAsync({
        mode: submitMode,
        repoUrl: repoUrl.trim(),
        ref: selectedRef || undefined,
        subdir: subdir || undefined,
        metadata,
        // Backend ignores `autoUpdate` for `mode: 'import'` and for SHA refs.
        // Still forward it cleanly so the value the user sees on the toggle
        // matches what gets persisted.
        autoUpdate: submitMode === 'plugin' ? autoUpdate : undefined,
      });
      if (submitMode === 'plugin') {
        const ack = result as PluginInstallAck;
        success('Installation started');
        navigate(routes.console.spacePlugin(spaceId, ack.pluginId));
        handleClose();
      } else {
        setReport(result as PluginInstallReport);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setInstallError({ code: err.code, message: err.message });
      } else {
        setInstallError({
          message: err instanceof Error ? err.message : 'Install failed',
        });
      }
      setPhase('ready');
    }
  }

  // Once we've successfully imported, swap the body for the report panel.
  if (report) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Import complete"
        size="lg"
        footer={<Button onClick={handleClose}>Done</Button>}
      >
        <ImportReportPanel report={report} />
      </Modal>
    );
  }

  const canInstall =
    phase === 'ready' &&
    (selectedSubdir !== undefined ||
      (exploreResult && exploreResult.candidates.length === 0));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Install plugin"
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={install.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInstall}
            isLoading={install.isPending}
            disabled={!canInstall || install.isPending}
          >
            Install
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground" htmlFor="install-repo">
            Repository URL
          </label>
          <div className="flex gap-2">
            <Input
              id="install-repo"
              placeholder="https://github.com/owner/repo"
              value={repoUrl}
              onChange={(e: { target: { value: string } }) =>
                handleRepoUrlChange(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!explore.isPending) handleExplore();
                }
              }}
              aria-invalid={submittedUrl && !isLikelyRepoUrl(repoUrl)}
              className="flex-1"
            />
            <Button
              onClick={handleExplore}
              disabled={!repoUrl || explore.isPending}
              isLoading={explore.isPending && phase === 'exploring' && !exploreResult}
            >
              <Search className="mr-1.5 h-3.5 w-3.5" />
              Explore
            </Button>
          </div>
        </div>

        {exploreError && (
          <InstallErrorBlock error={exploreError} onRetry={handleExplore} />
        )}

        {exploreResult && (
          <ExploreResultPanel
            result={exploreResult}
            selectedRef={selectedRef}
            onRefChange={setSelectedRef}
            selectedSubdir={selectedSubdir}
            onCandidateSelect={(c) => setSelectedSubdir(c.subdir)}
            manualSubdir={manualSubdir}
            onManualSubdirChange={setManualSubdir}
            reexploring={
              explore.isPending && phase === 'exploring' && !!exploreResult
            }
          />
        )}

        {phase === 'idle' && !exploreError && (
          <p className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-3 text-xs text-muted-foreground">
            Paste a git repository URL above and click <strong>Explore</strong> to
            see the available plugins, branches, and tags.
          </p>
        )}

        {exploreResult && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Mode</legend>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/40 transition-colors">
              <input
                type="radio"
                name="install-mode"
                value="plugin"
                checked={mode === 'plugin'}
                onChange={() => setMode('plugin')}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Install as plugin (stay in sync)
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Keep linked to the repository so you can sync updates or
                  uninstall to remove everything later.
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-accent/40 transition-colors">
              <input
                type="radio"
                name="install-mode"
                value="import"
                checked={mode === 'import'}
                onChange={() => setMode('import')}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-foreground">Copy once</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Import the resources into this space as standalone copies.
                  Further changes in the repository won't apply.
                </p>
              </div>
            </label>
          </fieldset>
        )}

        {exploreResult && mode === 'plugin' && exploreResult.refType !== 'sha' && (
          <div className="rounded-md border border-border p-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Label className="text-sm font-medium text-foreground">
                Auto-update
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Keep this plugin on the latest commit of the tracked ref.
                Applied on the next scheduled check.
              </p>
            </div>
            <Switch
              checked={autoUpdate}
              onCheckedChange={(checked: boolean) => setAutoUpdate(checked)}
            />
          </div>
        )}

        {installError && (
          <InstallErrorBlock error={installError} onRetry={handleInstall} />
        )}

        {phase === 'installing' && install.isPending && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Submitting installation…
          </div>
        )}
      </div>
    </Modal>
  );
}

function InstallErrorBlock({
  error,
  onRetry,
}: {
  error: ErrorState;
  onRetry: () => void;
}) {
  if (error.code === 'NO_GIT_CONNECTION') {
    return (
      <div className="rounded-md border border-status-warning-bg bg-status-warning-bg/40 p-3 text-sm">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 text-status-warning-fg" />
          <div className="flex-1">
            <p className="font-medium text-foreground">No connected git account</p>
            <p className="mt-1 text-muted-foreground">
              Private repositories need a connected GitHub or GitLab account. Add one in
              Credentials, then try again.
            </p>
            <a
              href={routes.console.credentials}
              className="mt-2 inline-flex items-center gap-1 text-foreground underline hover:no-underline"
            >
              Open Credentials
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }
  if (error.code === 'PRIVATE_SOURCE_UNSUPPORTED') {
    return (
      <ErrorBox message="Private repositories are supported only on GitHub and GitLab." />
    );
  }
  if (error.code === 'REF_NOT_FOUND') {
    return <ErrorBox message={error.message || "That ref doesn't exist on the remote."} />;
  }
  if (error.code === 'CLONE_FAILED' || error.code === 'NETWORK_ERROR') {
    return (
      <div className="rounded-md border border-status-error-bg bg-status-error-bg/40 p-3 text-sm">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 text-status-error-fg" />
          <div className="flex-1">
            <p className="font-medium text-foreground">Couldn't reach the repository</p>
            <p className="mt-1 text-muted-foreground">{error.message}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return <ErrorBox message={error.message} />;
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-status-error-bg bg-status-error-bg/40 p-3 text-sm flex items-start gap-2">
      <AlertCircle className="h-4 w-4 mt-0.5 text-status-error-fg" />
      <p className="text-foreground">{message}</p>
    </div>
  );
}
